import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  RefreshCw,
  Search,
  AlertCircle,
  Clock,
  Building2,
  Briefcase,
  Users,
  Bot,
  HelpCircle,
  BarChart3,
  LayoutGrid,
  LayoutList,
} from 'lucide-react';
import axiosInstance from '../../lib/api/axiosInstance';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { ContactEditDialog } from './ContactEditDialog';
import { ContactsTable } from './ContactsTable';
import { Contact, Client, Partner } from '../../types/contact';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ContactDetection } from './ContactDetection';

// Interface pour les statistiques des contacts
interface ContactStats {
  total: number;
  byType: {
    CLIENT: number;
    PARTNER: number;
    INTERNAL: number;
    SYSTEM: number;
    UNKNOWN: number;
  };
  clientContacts: number;
  partnerContacts: number;
  internalContacts: number;
  withPhone: number;
  withNotes: number;
}

export const ContactsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<keyof Contact>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({
    type: 'all',
    client: 'all',
    partner: 'all',
  });
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshClicked, setRefreshClicked] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fonction pour récupérer les contacts depuis l'API
  const fetchContacts = async () => {
    try {
      setLoading(true);
      setApiError(null);
      const response = await axiosInstance.get<Contact[]>('/contacts');
      setContacts(response.data);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching contacts:', error);
      setApiError(error.response?.data?.message || t('common.errorOccurred'));
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les clients depuis l'API
  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get<Client[]>('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Fonction pour récupérer les partenaires depuis l'API
  const fetchPartners = async () => {
    try {
      const response = await axiosInstance.get<Partner[]>('/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fonction pour rafraîchir les données
  const handleRefresh = async () => {
    setRefreshClicked(true);
    try {
      await fetchContacts();
    } catch (error) {
      console.error('Error refreshing contacts:', error);
    } finally {
      setRefreshClicked(false);
    }
  };

  // Charger les données au chargement du composant
  useEffect(() => {
    fetchContacts();
    fetchClients();
    fetchPartners();

    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(() => {
      fetchContacts();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Fonction pour valider les données du formulaire
  const validateForm = (data: Contact): boolean => {
    const newErrors: Record<string, string> = {};

    if (!data.name?.trim()) {
      newErrors.name = t('contact.errors.nameRequired');
    }

    if (!data.email?.trim()) {
      newErrors.email = t('contact.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = t('contact.errors.invalidEmail');
    }

    if (!data.contactType) {
      newErrors.contactType = t('contact.errors.typeRequired');
    }

    // Validation conditionnelle selon le type
    if (data.contactType === 'CLIENT' && !data.clientId) {
      newErrors.clientId = t('contact.errors.clientRequired');
    }

    if (data.contactType === 'PARTNER' && !data.partnerId) {
      newErrors.partnerId = t('contact.errors.partnerRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour gérer la soumission du formulaire
  const handleSubmit = async (formData: Contact): Promise<void> => {
    if (!validateForm(formData)) {
      return;
    }

    try {
      if (selectedContact?.id) {
        await axiosInstance.put(`/contacts/${selectedContact.id}`, formData);
      } else {
        await axiosInstance.post('/contacts', formData);
      }
      
      await fetchContacts();
      setOpen(false);
      setSelectedContact(null);
      setErrors({});
    } catch (error: any) {
      console.error('Error saving contact:', error);
      const errorMessage = error.response?.data?.message || t('common.errorOccurred');
      setApiError(errorMessage);
    }
  };

  // Fonction pour supprimer un contact
  const handleDelete = async (contact: Contact): Promise<void> => {
    if (!contact.id) return;

    if (window.confirm(t('contact.confirmDelete'))) {
      try {
        await axiosInstance.delete(`/contacts/${contact.id}`);
        await fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert(t('contact.deleteError'));
      }
    }
  };

  // Fonction pour trier les contacts
  const handleSort = (field: keyof Contact) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Générer les options de filtrage basées sur les contacts disponibles
  const filterOptions = useMemo(() => {
    return {
      type: ['CLIENT', 'PARTNER', 'INTERNAL', 'SYSTEM', 'UNKNOWN'],
      client: [...new Set(contacts
        .filter(c => c.clientId)
        .map(c => c.clientId?.toString() || '')
        .filter(Boolean))],
      partner: [...new Set(contacts
        .filter(c => c.partnerId)
        .map(c => c.partnerId?.toString() || '')
        .filter(Boolean))]
    };
  }, [contacts]);

  // Appliquer les filtres et la recherche aux contacts
  const filteredContacts = useMemo(() => {
    return contacts
      .filter(contact => {
        // Filtrer par chaîne de recherche
        const searchMatches = searchQuery === "" || 
          contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (contact.role && contact.role.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (contact.phone && contact.phone.toLowerCase().includes(searchQuery.toLowerCase()));

        // Appliquer les filtres de sélection
        const typeMatches = filters.type === 'all' || contact.contactType === filters.type;
        const clientMatches = filters.client === 'all' || contact.clientId?.toString() === filters.client;
        const partnerMatches = filters.partner === 'all' || contact.partnerId?.toString() === filters.partner;

        return searchMatches && typeMatches && clientMatches && partnerMatches;
      })
      .sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (!aValue && !bValue) return 0;
        if (!aValue) return sortDirection === 'asc' ? 1 : -1;
        if (!bValue) return sortDirection === 'asc' ? -1 : 1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return 0;
      });
  }, [contacts, searchQuery, filters, sortField, sortDirection]);

  // Générer des statistiques pour le tableau de bord
  const stats: ContactStats = useMemo(() => {
    const stats = {
      total: contacts.length,
      byType: {
        CLIENT: contacts.filter(c => c.contactType === 'CLIENT').length,
        PARTNER: contacts.filter(c => c.contactType === 'PARTNER').length,
        INTERNAL: contacts.filter(c => c.contactType === 'INTERNAL').length,
        SYSTEM: contacts.filter(c => c.contactType === 'SYSTEM').length,
        UNKNOWN: contacts.filter(c => c.contactType === 'UNKNOWN').length
      },
      clientContacts: contacts.filter(c => c.clientId).length,
      partnerContacts: contacts.filter(c => c.partnerId).length,
      internalContacts: contacts.filter(c => c.userId).length,
      withPhone: contacts.filter(c => c.phone && c.phone.trim() !== '').length,
      withNotes: contacts.filter(c => c.notes && c.notes.trim() !== '').length
    };
    return stats;
  }, [contacts]);

  // Rendre l'entité associée au contact (client, partenaire, etc.)
  const renderContactEntity = (contact: Contact) => {
    switch(contact.contactType) {
      case 'CLIENT':
        return (
          <div className="flex items-center space-x-1">
            <Building2 className="h-3.5 w-3.5 text-blue-500" />
            <span>{contact.clientName || 'Non associé'}</span>
          </div>
        );
      
      case 'PARTNER':
        return (
          <div className="flex items-center space-x-1">
            <Briefcase className="h-3.5 w-3.5 text-amber-500" />
            <span>{contact.partnerName || 'Non associé'}</span>
          </div>
        );

      case 'INTERNAL':
        return (
          <div className="flex items-center space-x-1">
            <Users className="h-3.5 w-3.5 text-green-500" />
            <span>{contact.userName || 'Non associé'}</span>
          </div>
        );

      case 'SYSTEM':
        return (
          <div className="flex items-center space-x-1">
            <Bot className="h-3.5 w-3.5 text-purple-500" />
            <span>{t('contact.system')}</span>
          </div>
        );

      default:
        return (
          <div className="flex items-center space-x-1">
            <HelpCircle className="h-3.5 w-3.5 text-gray-500" />
            <span>{t('contact.unknown')}</span>
          </div>
        );
    }
  };

  // Afficher un squelette de chargement si les données sont en cours de chargement
  if (loading && contacts.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle>{t('contact.management')}</CardTitle>
            <CardDescription>{t('common.loading')}</CardDescription>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="space-y-4">
              <Skeleton className="h-[150px] w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Afficher un message d'erreur s'il y en a
  if (apiError) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <CardTitle>{t('contact.management')}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Afficher le composant principal
  return (
    <div className="p-6 space-y-6">
      {/* Carte principale avec statistiques */}
      <Card className="border-none shadow-sm dark:shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <CardTitle className="text-2xl">{t('contact.management')}</CardTitle>
              {lastUpdated && (
                <CardDescription className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {t('common.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={loading || refreshClicked}
                className="h-9 w-9 rounded-full transition-all"
                aria-label={t('common.refresh')}
              >
                <RefreshCw className={`h-4 w-4 ${(loading || refreshClicked) ? 'animate-spin text-primary' : ''}`} />
                <span className="sr-only">{t('common.refresh')}</span>
              </Button>
              <Button 
                onClick={() => {
                  setSelectedContact(null);
                  setErrors({});
                  setOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('contact.addNew')}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {/* Tableau de bord avec statistiques */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Cartes récapitulatives */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('contact.totalContacts')}</p>
                      <h3 className="text-3xl font-bold">{stats.total}</h3>
                    </div>
                    <Users className="h-8 w-8 text-muted-foreground/60" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('contact.clientContacts')}</p>
                      <h3 className="text-3xl font-bold">{stats.byType.CLIENT}</h3>
                    </div>
                    <Building2 className="h-8 w-8 text-blue-500/60" />
                  </div>
                  <div className="mt-2">
                    <Progress 
                      value={(stats.byType.CLIENT / Math.max(1, stats.total)) * 100} 
                      className="h-1 bg-muted/50"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('contact.partnerContacts')}</p>
                      <h3 className="text-3xl font-bold">{stats.byType.PARTNER}</h3>
                    </div>
                    <Briefcase className="h-8 w-8 text-amber-500/60" />
                  </div>
                  <div className="mt-2">
                    <Progress 
                      value={(stats.byType.PARTNER / Math.max(1, stats.total)) * 100} 
                      className="h-1 bg-muted/50"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('contact.internalContacts')}</p>
                      <h3 className="text-3xl font-bold">{stats.byType.INTERNAL}</h3>
                    </div>
                    <Users className="h-8 w-8 text-green-500/60" />
                  </div>
                  <div className="mt-2">
                    <Progress 
                      value={(stats.byType.INTERNAL / Math.max(1, stats.total)) * 100} 
                      className="h-1 bg-muted/50"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Distribution par type */}
            <div className="lg:col-span-4">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{t('contact.typeDistribution')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(stats.byType).map(([type, count]) => (
                      <div key={type}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{t(`contact.type.${type.toLowerCase()}`)}</span>
                          <span className="text-sm text-muted-foreground">{count}</span>
                        </div>
                        <Progress 
                          value={(count / Math.max(1, stats.total)) * 100} 
                          className={`h-2 ${getProgressColorByType(type as keyof typeof stats.byType)}`} 
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des contacts */}
      <Card className="border-none shadow-sm dark:shadow-lg overflow-hidden">
        <CardHeader className="pb-0">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">{t('contact.list')}</CardTitle>
            </div>
            <Tabs 
              value={viewMode} 
              onValueChange={(value) => setViewMode(value as "table" | "card")}
              className="w-full sm:w-auto"
            >
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="table" className="flex-1 sm:flex-initial flex items-center gap-2">
                  <LayoutList className="h-4 w-4" />
                  {t('contact.tableView')}
                </TabsTrigger>
                <TabsTrigger value="card" className="flex-1 sm:flex-initial flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4" />
                  {t('contact.cardView')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={t('contact.searchPlaceholder')}
                className="pl-8 bg-muted/50 focus:bg-background transition-colors"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Filtre par type de contact */}
              <Select
                value={filters.type}
                onValueChange={(value) => setFilters({...filters, type: value})}
              >
                <SelectTrigger className="h-9 w-[150px] text-sm bg-background border-input">
                  <SelectValue placeholder={t('contact.filterByType')}>
                    {filters.type === 'all' ? t('contact.allTypes') : t(`contact.type.${filters.type.toLowerCase()}`)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('contact.allTypes')}</SelectItem>
                  {filterOptions.type.map(type => (
                    <SelectItem key={type} value={type}>
                      {t(`contact.type.${type.toLowerCase()}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Filtre par client */}
              {clients.length > 0 && (
                <Select
                  value={filters.client}
                  onValueChange={(value) => setFilters({...filters, client: value})}
                >
                  <SelectTrigger className="h-9 w-[150px] text-sm bg-background border-input">
                    <SelectValue placeholder={t('contact.filterByClient')}>
                      {filters.client === 'all' ? t('contact.allClients') : clients.find(c => c.id.toString() === filters.client)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('contact.allClients')}</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Filtre par partenaire */}
              {partners.length > 0 && (
                <Select
                  value={filters.partner}
                  onValueChange={(value) => setFilters({...filters, partner: value})}
                >
                  <SelectTrigger className="h-9 w-[150px] text-sm bg-background border-input">
                    <SelectValue placeholder={t('contact.filterByPartner')}>
                      {filters.partner === 'all' ? t('contact.allPartners') : partners.find(p => p.id.toString() === filters.partner)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('contact.allPartners')}</SelectItem>
                    {partners.map(partner => (
                      <SelectItem key={partner.id} value={partner.id.toString()}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {searchQuery && (
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                {t('contact.foundResults', { found: filteredContacts.length, total: contacts.length })}
              </p>
            )}
          </div>

          <Tabs value={viewMode} className="mt-0">
            {/* Vue tableau */}
            <TabsContent value="table" className="m-0 pt-4">
              <ContactsTable
                contacts={filteredContacts}
                onEdit={(contact) => {
                  setSelectedContact(contact);
                  setErrors({});
                  setOpen(true);
                }}
                onDelete={handleDelete}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </TabsContent>
            
            {/* Vue carte */}
            <TabsContent value="card" className="m-0 pt-4">
              {filteredContacts.length === 0 ? (
                <div className="text-center py-10 border border-dashed rounded-lg">
                  <Users className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
                  <h3 className="text-lg font-medium mb-1">{t('contact.noContactsFound')}</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-4">
                    {searchQuery ? t('contact.tryDifferentSearch') : t('contact.addFirstContact')}
                  </p>
                  <Button onClick={() => {
                    setSelectedContact(null);
                    setOpen(true);
                  }}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('contact.addNew')}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContacts.map(contact => (
                    <Card key={contact.id} className="overflow-hidden border hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between">
                          <CardTitle className="truncate text-base">{contact.name}</CardTitle>
                          <Badge variant="secondary" className={`${getBadgeColorByType(contact.contactType)}`}>
                            {contact.contactType ? t(`contact.type.${contact.contactType.toLowerCase()}`) : t('contact.type.unknown')}
                          </Badge>
                        </div>
                        <CardDescription className="truncate">
                          {contact.email}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="space-y-2 text-sm">
                          <div>{renderContactEntity(contact)}</div>
                          
                          {contact.role && (
                            <div className="text-muted-foreground">{contact.role}</div>
                          )}
                          
                          {contact.phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              {contact.phone}
                            </div>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter className="justify-end pt-1 pb-3 gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setSelectedContact(contact);
                            setOpen(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDelete(contact)}
                          className="text-destructive border-destructive/20 hover:bg-destructive/10"
                        >
                          {t('common.delete')}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card className="border-none shadow-sm dark:shadow-lg">
        <ContactDetection/>
      </Card>

      {/* Dialogue d'édition/création de contact */}
      <ContactEditDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedContact(null);
          setErrors({});
        }}
        onSubmit={handleSubmit}
        contact={selectedContact}
        errors={errors}
        clients={clients}
        partners={partners}
      />
    </div>
  );
};

// Fonction utilitaire pour obtenir la couleur du badge en fonction du type de contact
function getBadgeColorByType(type: string | undefined): string {
  if (!type) return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  
  switch (type) {
    case 'CLIENT':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'PARTNER':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'INTERNAL':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'SYSTEM':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

// Fonction utilitaire pour obtenir la couleur de la barre de progression en fonction du type
function getProgressColorByType(type: string): string {
  switch (type) {
    case 'CLIENT':
      return 'bg-blue-500';
    case 'PARTNER':
      return 'bg-amber-500';
    case 'INTERNAL':
      return 'bg-green-500';
    case 'SYSTEM':
      return 'bg-purple-500';
    default:
      return 'bg-gray-500';
  }
}
